"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  buildAppBounceAttempts,
  buildAppBounceUrl,
  hasBounced,
  isFacebookPostLink,
  markBounced,
  readBouncedPasteboardToken,
} from "./appBounce";
import {
  BrokenInAppBrowser,
  cannotEscape,
  opensStoreLinksOnTap,
} from "./detect";
import { buildEscapeAttempts, isBlockedDestination } from "./escapeAttempts";
import { mustOpenInBrowser } from "./mustOpenInBrowser";
import { runInAppEscape } from "./runEscape";

type InAppEscapeState = {
  browser: BrokenInAppBrowser;
  canBounceToApp: boolean;
  returnedFromBounce: boolean;
  bouncedPasteboardToken: string | null;
  recordClickOnStoreTap: RefObject<(() => void) | null>;
};

// Null means "this browser follows store links normally", so a tree with no
// provider behaves as it did before this workaround existed.
const InAppEscapeContext = createContext<InAppEscapeState | null>(null);

const subscribeToNothing = () => () => {};

export function InAppEscapeProvider({
  browser,
  canBounceToApp,
  children,
}: {
  browser: BrokenInAppBrowser | null;
  canBounceToApp: boolean;
  children: ReactNode;
}) {
  const recordClickOnStoreTap = useRef<(() => void) | null>(null);

  // The server cannot see these. useSyncExternalStore uses the server value
  // during hydration, which avoids a mismatch. Not watched: a tap that bounces
  // marks this page too, just before it leaves, and that is not a return.
  const returnedFromBounce = useSyncExternalStore(
    subscribeToNothing,
    () => canBounceToApp && hasBounced(window.location.href),
    () => false
  );
  const bouncedPasteboardToken = useSyncExternalStore(
    subscribeToNothing,
    () =>
      returnedFromBounce
        ? readBouncedPasteboardToken(window.location.href)
        : null,
    () => null
  );

  const state = useMemo(
    () =>
      browser
        ? {
            browser,
            canBounceToApp,
            returnedFromBounce,
            bouncedPasteboardToken,
            recordClickOnStoreTap,
          }
        : null,
    [browser, canBounceToApp, returnedFromBounce, bouncedPasteboardToken]
  );

  return (
    <InAppEscapeContext.Provider value={state}>
      {children}
    </InAppEscapeContext.Provider>
  );
}

/** Stops a running chain on unmount, so that no caller holds refs for it. */
function useEscapeDisposer(): RefObject<(() => void) | null> {
  const disposeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      disposeRef.current?.();
      disposeRef.current = null;
    };
  }, []);

  return disposeRef;
}

/**
 * The bounce's part in a store escape. After a tap bounce the caller's own
 * store redirect works, so nothing should run. After a bounce that gave up, a
 * store tap is what shows the visitor stayed, so it records the click.
 */
function appBounceHandlesStoreEscape(
  { canBounceToApp, recordClickOnStoreTap }: InAppEscapeState,
  fromTap: boolean
): boolean {
  if (!canBounceToApp) return false;
  if (hasBounced(window.location.href)) return true;

  if (fromTap) {
    recordClickOnStoreTap.current?.();
    recordClickOnStoreTap.current = null;
  }
  return false;
}

type StartEscapeOptions = {
  /**
   * Set when calling straight out of a tap handler. Awaiting anything before
   * the call spends the user activation and iOS then drops the navigation.
   */
  fromTap?: boolean;
  /** Called when the page is still here and should stop waiting. */
  onSettled?: () => void;
  /** Falls back to the click the reporter published for this page. */
  clickId?: string | null;
};

export type InAppEscape = {
  browser: BrokenInAppBrowser;
  /**
   * False when nothing was started, which is the common case even here: most
   * links point at a web page, and those load fine. The caller then does what
   * it does everywhere else.
   */
  start: (destination: string, options?: StartEscapeOptions) => boolean;
};

/**
 * Null unless the visitor is in a browser that refuses store URLs. Callers
 * branch on that once and are otherwise unchanged:
 *
 *   const inAppEscape = useInAppEscape();
 *   if (inAppEscape?.start(url, { fromTap: true })) return;
 *   window.location.replace(url);
 */
export function useInAppEscape(): InAppEscape | null {
  const state = useContext(InAppEscapeContext);
  const disposeRef = useEscapeDisposer();

  return useMemo<InAppEscape | null>(() => {
    if (!state) return null;
    const { browser } = state;

    return {
      browser,
      start: (destination, { fromTap = false, onSettled, clickId } = {}) => {
        if (!isBlockedDestination(destination)) return false;

        // Nothing to fire where every way out is refused; those visitors get
        // useMustOpenInBrowser instead.
        if (cannotEscape(browser)) return false;

        if (appBounceHandlesStoreEscape(state, fromTap)) return false;

        // The caller's own navigation works from a tap here, and an escape
        // would only get in its way.
        if (fromTap && opensStoreLinksOnTap(browser)) return false;

        // A second tap restarts the chain rather than racing two of them.
        disposeRef.current?.();
        disposeRef.current = runInAppEscape({
          browser,
          attempts: buildEscapeAttempts(browser, destination),
          trigger: fromTap ? "tap" : "auto",
          onExhausted: onSettled,
          onReturned: onSettled,
          clickId,
        });
        return true;
      },
    };
  }, [state, disposeRef]);
}

export type AppBounce = {
  /**
   * Leaves through a page that opens the app if it is installed: the app or
   * the browser it opens records the click. Calls recordClick when the visitor
   * stays here and taps the store button instead. A visitor who stays and taps
   * nothing is not counted. On a profile link it leaves nothing and calls
   * onWaitingForTap: only a tap can open the app there, see bounceToApp. False
   * when nothing was started. See appBounce.ts.
   *
   * Call it before recording a click or marking the redirect as attempted. A
   * click recorded first counts the visitor twice, and with the mark set the
   * link page the bounce comes back to would not redirect to the store.
   */
  start: (
    destination: string,
    options?: {
      onSettled?: () => void;
      onWaitingForTap?: () => void;
      recordClick?: () => void;
    }
  ) => boolean;
  /**
   * From a tap: opens the app, or comes back to this link, which then opens
   * the store. Pass the pasteboard token copy-link just put on the clipboard.
   */
  bounceToApp: (pasteboardToken?: string | null) => void;
  returnedFromBounce: boolean;
  bouncedPasteboardToken: string | null;
};

/**
 * Null where the page must not bounce: anywhere but Facebook on iOS, or on a
 * host the bounce page refuses. See appBounceServer.ts and appBounce.ts.
 *
 * A screen that waits for a tap anyway (App Preview, copy-link) bounces on
 * that tap and records no click before it: the app or the page the bounce
 * comes back to records it. That page has returnedFromBounce set, which means
 * the app is missing. The tap still allows a store redirect there, so the
 * screen goes on to the store instead of waiting for another tap. It records
 * its click with bouncedPasteboardToken when there is one.
 */
export function useAppBounce(): AppBounce | null {
  const state = useContext(InAppEscapeContext);
  const disposeRef = useEscapeDisposer();

  return useMemo<AppBounce | null>(() => {
    if (!state?.canBounceToApp) return null;
    const {
      browser,
      returnedFromBounce,
      bouncedPasteboardToken,
      recordClickOnStoreTap,
    } = state;

    return {
      start: (
        destination,
        { onSettled, onWaitingForTap, recordClick } = {}
      ) => {
        if (!isBlockedDestination(destination)) return false;
        if (hasBounced(window.location.href)) return false;

        // x-safari cannot work here and would block the tap bounce too.
        if (!isFacebookPostLink(window.location.href)) {
          onWaitingForTap?.();
          return true;
        }

        disposeRef.current?.();
        disposeRef.current = runInAppEscape({
          browser,
          attempts: buildAppBounceAttempts(window.location.href),
          trigger: "auto",
          // A refused x-safari blocks script navigation in this tab, so a tap
          // cannot bounce any more. The store button's plain link still works.
          //
          // Facebook's prompt does not hide the page, so the chain also ends
          // while the visitor is still reading it. A click recorded now would
          // count them twice: here and in Safari or the app. Only a store tap
          // shows they stayed.
          onExhausted: () => {
            recordClickOnStoreTap.current = recordClick ?? null;
            onSettled?.();
          },
          onReturned: onSettled,
        });
        return true;
      },
      bounceToApp: (pasteboardToken) => {
        markBounced(window.location.href, pasteboardToken);
        window.location.href = buildAppBounceUrl(window.location.href);
      },
      returnedFromBounce,
      bouncedPasteboardToken,
    };
  }, [state, disposeRef]);
}

/** onClick for the button shown after onWaitingForTap. See bounceToApp. */
export function useBounceToAppTap():
  ((event: MouseEvent<HTMLAnchorElement>) => void) | undefined {
  const appBounce = useAppBounce();

  return useMemo(() => {
    if (!appBounce) return undefined;

    return (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      appBounce.bounceToApp();
    };
  }, [appBounce]);
}

/** See mustOpenInBrowser. */
export function useMustOpenInBrowser(
  destination: string | null | undefined
): boolean {
  const state = useContext(InAppEscapeContext);
  return mustOpenInBrowser(state?.browser ?? null, destination);
}

/**
 * onClick for an anchor pointing at a store, whose href these webviews refuse.
 *
 * Undefined everywhere else, so the anchor stays an ordinary link — including
 * with JS off, since the href itself is never replaced.
 */
export function useStoreLinkEscape(
  destination: string | null | undefined
): ((event: MouseEvent<HTMLAnchorElement>) => void) | undefined {
  const inAppEscape = useInAppEscape();

  return useMemo(() => {
    if (!inAppEscape || !destination) return undefined;
    // No handler at all for an href the webview opens normally.
    if (!isBlockedDestination(destination)) return undefined;

    return (event: MouseEvent<HTMLAnchorElement>) => {
      // preventDefault only once the escape has taken over, or a declined tap
      // would leave a dead button.
      if (!inAppEscape.start(destination, { fromTap: true })) return;
      event.preventDefault();
    };
  }, [inAppEscape, destination]);
}
